import { useEffect, useRef, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { testimonialsPageData, lineUrl } from "@/mocks/siteData";
import SEO from "@/components/base/SEO";

function useRevealOnScroll() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
          }
        });
      },
      { threshold: 0.1 }
    );
    el.querySelectorAll(".reveal").forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, []);
  return ref;
}

export default function Testimonials() {
  const revealRef = useRevealOnScroll();
  const data = testimonialsPageData;
  const categories = data.categories;
  const [activeCategory, setActiveCategory] = useState("all");

  const filteredCategories = useMemo(() => {
    if (activeCategory === "all") return categories;
    return categories.filter((c) => c.id === activeCategory);
  }, [categories, activeCategory]);

  return (
    <>
      <SEO
        title="客戶見證｜時光逆行駭客｜真實逆齡經驗分享"
        description="不同年齡、不同生命階段的客戶在時光逆行駭客曾驛翔院長陪伴下的個人感受。包含型男醫美、青春痘、女性醫美與熟齡慢老等不同族群的逆齡經驗分享。"
        keywords="客戶見證,逆齡經驗,醫美見證,曾驛翔,時光逆行駭客,抗衰老分享"
        canonicalPath="/testimonials"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "客戶見證｜時光逆行駭客",
          "description": "不同年齡族群的逆齡經驗分享，個人主觀感受，不代表所有人皆有相同結果。",
        }}
      />
    <div className="min-h-screen bg-abyss-900 circuit-bg" ref={revealRef}>
      {/* ===== HERO ===== */}
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="absolute inset-0 circuit-bg opacity-20" />
        <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="text-center max-w-4xl mx-auto">
            <span className="section-label reveal opacity-0 translate-y-6 transition-all duration-700">
              {data.heroTagline}
            </span>
            <h1 className="mt-5 text-[2rem] leading-[1.3] md:text-[2.75rem] md:leading-[1.25] font-bold text-ghost reveal opacity-0 translate-y-6 transition-all duration-700 delay-100">
              {data.heroTitle}
              <br />
              <span className="text-gradient-gold">{data.heroTitleAccent}</span>
            </h1>
            <p className="mt-6 text-ghost-muted text-[1.1875rem] leading-[1.75] reveal opacity-0 translate-y-6 transition-all duration-700 delay-200">
              {data.heroDesc}
            </p>
          </div>
        </div>
      </section>

      {/* ===== LEGAL NOTICE ===== */}
      <section className="py-8 md:py-10 bg-abyss-800/50">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="reveal opacity-0 translate-y-6 transition-all duration-700">
            <div className="card-cyber p-5 md:p-6 border-l-4 border-l-neon-cyan/40">
              <div className="flex items-start gap-3">
                <i className="ri-shield-check-line text-neon-cyan text-xl shrink-0 mt-0.5" />
                <p className="text-ghost-dim text-[1rem] leading-[1.7]">{data.legalNote}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CATEGORY TABS ===== */}
      <section className="pt-10 md:pt-12 pb-4">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="reveal opacity-0 translate-y-6 transition-all duration-700">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveCategory("all")}
                className={`px-4 py-2 rounded-lg text-base font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeCategory === "all"
                    ? "bg-cyber-gold/15 text-cyber-gold border border-cyber-gold/30"
                    : "bg-abyss-700/50 text-ghost-dim border border-abyss-600 hover:border-ghost-dim/30 hover:text-ghost"
                }`}
              >
                全部類別
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-2 rounded-lg text-base font-medium transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                    activeCategory === cat.id
                      ? "text-cyber-gold border border-cyber-gold/30"
                      : "text-ghost-dim border border-abyss-600 hover:border-ghost-dim/30 hover:text-ghost"
                  }`}
                  style={
                    activeCategory === cat.id
                      ? { background: `${cat.accent}15` }
                      : { background: "rgba(31, 35, 47, 0.5)" }
                  }
                >
                  <i className={`${cat.icon} text-base`} style={{ color: cat.accent }} />
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIAL CATEGORIES ===== */}
      <section className="py-8 md:py-12">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="space-y-16 md:space-y-20">
            {filteredCategories.map((cat) => (
              <div key={cat.id}>
                {/* Category header */}
                <div className="mb-8 md:mb-10 reveal opacity-0 translate-y-6 transition-all duration-700">
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: `${cat.accent}12`, border: `1px solid ${cat.accent}25` }}
                    >
                      <i className={`${cat.icon} text-lg`} style={{ color: cat.accent }} />
                    </div>
                    <h2 className="text-[1.375rem] md:text-[1.625rem] font-bold text-ghost">
                      {cat.label}
                    </h2>
                  </div>
                  <p className="text-ghost-muted text-[1.0625rem] leading-[1.7] pl-[52px] md:pl-0 md:ml-[52px]">
                    {cat.desc}
                  </p>
                </div>

                {/* Story cards grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {cat.stories.map((story, sidx) => (
                    <div
                      key={sidx}
                      className="group card-cyber p-6 md:p-7 reveal opacity-0 translate-y-6 transition-all duration-700 hover:-translate-y-2 hover:border-cyber-gold/30"
                      style={{ transitionDelay: `${sidx * 100}ms` }}
                    >
                      {/* Identity */}
                      <div className="flex items-center gap-3 mb-4">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 group-hover:scale-110"
                          style={{ background: `${cat.accent}10`, border: `1px solid ${cat.accent}20` }}
                        >
                          <i className="ri-user-line text-base" style={{ color: cat.accent }} />
                        </div>
                        <div>
                          <p className="text-ghost font-medium text-[1.0625rem] group-hover:text-cyber-gold transition-colors duration-500">
                            {story.identity}
                          </p>
                          <p className="text-ghost-dim text-base">{story.concern}</p>
                        </div>
                      </div>

                      {/* Quote */}
                      <i className="ri-double-quotes-l text-cyber-gold/20 text-2xl mb-2 block transition-transform duration-500 group-hover:scale-110" />

                      {/* Story content */}
                      <div className="space-y-3">
                        <div>
                          <p className="font-mono text-neon-cyan text-base mb-1">// BEFORE</p>
                          <p className="text-ghost-muted text-[1.0625rem] leading-[1.7] line-clamp-3">
                            {story.before}
                          </p>
                        </div>
                      </div>

                      {/* Compliance note */}
                      <div
                        className="mt-5 px-3 py-2 rounded-lg text-base"
                        style={{
                          background: `${cat.accent}08`,
                          color: `${cat.accent}cc`,
                          border: `1px solid ${cat.accent}15`,
                        }}
                      >
                        {story.note}
                      </div>

                      {/* Read more link */}
                      <div className="mt-5 pt-5 border-t border-abyss-700">
                        <Link
                          to={`/testimonials/${(story as any).id}`}
                          className="inline-flex items-center gap-1.5 text-base font-medium hover:gap-3 transition-all"
                          style={{ color: cat.accent }}
                        >
                          閱讀完整故事
                          <i className="ri-arrow-right-line" />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BOTTOM CTA ===== */}
      <section className="py-20 md:py-28">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="card-cyber p-10 md:p-16 text-center reveal opacity-0 translate-y-6 transition-all duration-700">
            <span className="section-label">{data.bottomCta.label}</span>
            <h2 className="mt-5 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
              {data.bottomCta.title}
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={lineUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-line text-[1.0625rem]"
              >
                <i className="ri-line-fill" />
                {data.bottomCta.ctaLine}
              </a>
              <Link to="/booking" className="btn-gold text-[1.0625rem]">
                <i className="ri-calendar-check-line" />
                {data.bottomCta.ctaBooking}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  </>
  );
}